import { useCallback, useMemo } from 'react';
import { fitColumnWidths } from '../utils/fitColumnWidths';

/**
 * useFitColumnsToScreen — the page-side glue behind every table's
 * `Actions ▾ → Fit columns to screen` (owner ruling, Pete, 2026-09-28:
 * *"reset column widths should use the same code and changes as Fit
 * columns to screen on the Breakdown page. same for other tables"*, itself
 * applying his 2026-09-27 Breakdown ruling — *"reset colum widths should be
 * changed to fit column widths so that we try our best to fit on desktop
 * view. Truncate where needed to try and make it fit"*, and **"Replace
 * Reset with Fit, and remember"**).
 *
 * 🔀 **EXTRACTED 2026-09-28** so the four tables that now offer this menu
 * item (the Breakdown, the two order tables, the hires Table) do not each
 * carry their own copy of the fitted-map + "already fits" comparison + the
 * one-write-path loop through the width hook's own `setWidth` — the exact
 * duplication `npm run check-duplication` exists to flag. `GagBreakdown.js`
 * still builds its own inline (its `fittedColumnWidths` / `columnsAlreadyFit`
 * / `fitColumnsToScreen` predate this hook and are byte-equivalent to what
 * this hook computes); every new caller should use this hook rather than
 * copy that page's inline version a fourth time.
 *
 * @param {Array<{key: string, width: number, min: number}>} columns
 * @param {Record<string, number>} widths       the LIVE width map (the
 *        hook's own `widths`, or a page-lifted equivalent).
 * @param {(key: string, px: number) => void} setWidth  the ONE write path —
 *        `useColumnWidths`' own `setWidth`. Calling it once per column lets
 *        React batch the whole fit into one render and one storage write.
 * @param {number} maxWidth   the shared ceiling every column obeys.
 * @param {number|null} available  the table's OWN measured scrollport width
 *        (never `window.innerWidth` — see `fitColumnWidths`'s own docblock).
 *        `null` before anything has been measured.
 * @returns {{
 *   fitColumnsToScreen: () => void,
 *   canFitColumns: boolean,
 *   alreadyFit: boolean,
 * }}
 *   `canFitColumns` is `available != null` — whether there is anything to
 *   fit to at all (the menu item is offered but disabled without it, same
 *   as the Breakdown). `alreadyFit` is true when every live width already
 *   equals the fresh fit, which is what disables the item on a table that
 *   is already fitted — clicking it would write nothing.
 */
export default function useFitColumnsToScreen({ columns, widths, setWidth, maxWidth, available }) {
  const fitted = useMemo(
    () => fitColumnWidths(available, columns, maxWidth),
    [available, columns, maxWidth]
  );

  const alreadyFit = useMemo(
    () => fitted != null && columns.every((c) => widths[c.key] === fitted[c.key]),
    [fitted, columns, widths]
  );

  const fitColumnsToScreen = useCallback(() => {
    if (!fitted) return;
    for (const c of columns) setWidth(c.key, fitted[c.key]);
  }, [fitted, columns, setWidth]);

  return { fitColumnsToScreen, canFitColumns: fitted != null, alreadyFit };
}
